import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Wifi, LogOut, LayoutDashboard, Gift } from "lucide-react";
import { CountryPicker } from "./CountryPicker";
import { useAuth, homeFor } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import { Button } from "./ui/button";

const NAV = [
  ["/internet/fibre", "Fibre"], ["/internet/5g", "5G"], ["/internet/lte", "LTE"],
  ["/hosting", "Hosting"], ["/domains", "Domains"], ["/website-builder", "Free website"], ["/partners", "Partners"],
];

export const Logo = ({ light }) => (
  <Link to="/" data-testid="brand-logo" className="group flex items-center gap-2">
    <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-white shadow-[0_6px_16px_-6px_rgba(240,50,110,0.7)] transition-transform duration-300 group-hover:rotate-[-8deg] group-hover:scale-105">
      <Wifi className="h-5 w-5" />
    </span>
    <span className={`font-display text-xl font-extrabold tracking-tight ${light ? "text-white" : "text-foreground"}`}>
      HostAfricair<span className="text-primary">.com</span>
    </span>
  </Link>
);

const PromoStrip = () => {
  const { country } = useCountry();
  return (
    <div className="brand-bar text-white" data-testid="promo-strip">
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-1.5 text-xs font-semibold sm:text-sm">
        <Gift className="h-3.5 w-3.5" />
        <span className="truncate">Free {country?.free_tld || ".africa"} domain with every package<span className="hidden sm:inline"> · Month-to-month, cancel anytime</span></span>
      </div>
    </div>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <header data-testid="global-header" className="sticky top-0 z-40">
      <PromoStrip />
      <div className="border-b bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
          <Logo />
          <nav className="ml-4 hidden items-center gap-0.5 lg:flex" data-testid="main-nav">
            {NAV.map(([to, label]) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-accent text-primary" : "text-foreground/80 hover:bg-muted hover:text-primary"}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <CountryPicker />
            {user ? (
              <div className="hidden items-center gap-1 sm:flex" data-testid="nav-portal-links">
                <Button size="sm" variant="ghost" className="whitespace-nowrap rounded-full" onClick={() => nav(homeFor(user))} data-testid="nav-dashboard-btn">
                  <LayoutDashboard className="h-4 w-4 xl:mr-1.5" /><span className="hidden xl:inline">My account</span>
                </Button>
                <Button size="icon" variant="ghost" className="rounded-full text-muted-foreground" onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button size="sm" className="hidden rounded-full px-5 sm:inline-flex" onClick={() => nav("/login")} data-testid="nav-login-btn">Sign in</Button>
            )}
            <button className="rounded-full p-2 lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="border-t px-4 pb-4 lg:hidden" data-testid="mobile-menu">
            {NAV.map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="block py-2.5 text-sm font-medium">{label}</Link>
            ))}
            <Link to={user ? homeFor(user) : "/login"} onClick={() => setOpen(false)} className="block py-2.5 text-sm font-semibold text-primary">
              {user ? "My account" : "Sign in"}
            </Link>
            {user && (
              <button onClick={async () => { setOpen(false); await logout(); nav("/"); }} className="block py-2.5 text-sm font-medium text-muted-foreground" data-testid="mobile-logout-btn">Sign out</button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
